import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit, X, Upload, Star } from "lucide-react";

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => api.get("/admin/testimonials").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer ce témoignage ?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Supprimé"); load();
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <Link to="/admin" className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Retour</Link>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Témoignages</h1>
          <button onClick={() => setModal({})} data-testid="add-testimonial-btn" className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>Nouveau témoignage</button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {items.length === 0 && <div className="col-span-full p-12 text-center text-slate-500 font-mono">Aucun témoignage.</div>}
          {items.map(t => (
            <div key={t.id} className="bg-white border border-slate-900/15 p-6" data-testid={`row-testimonial-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display font-bold">{t.name}</div>
                  <div className="text-xs text-slate-500">{[t.role, t.company].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="flex gap-1">{Array.from({length: t.rating || 5}).map((_,i) => <Star key={`star-${t.id}-${i}`} className="w-3 h-3 text-[#FFC107]" fill="currentColor"/>)}</div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed italic">"{t.content}"</p>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-900/10">
                <span className="text-xs font-mono text-slate-500">{t.published ? "Publié" : "Masqué"} · Ordre {t.order}</span>
                <div className="flex gap-1">
                  <button onClick={() => setModal({ item: t })} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white"><Edit className="w-3 h-3"/></button>
                  <button onClick={() => remove(t.id)} className="p-2 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 className="w-3 h-3"/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
      {modal && <TModal item={modal.item} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function TModal({ item, onClose, onSaved }) {
  const [f, setF] = useState(item || { name: "", role: "", company: "", content: "", rating: 5, avatar_url: "", published: true, order: 100 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, rating: parseInt(f.rating)||5, order: parseInt(f.order)||100 };
      if (item) await api.put(`/admin/testimonials/${item.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Enregistré"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white border border-slate-900 w-full max-w-lg p-8 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between"><h2 className="font-display font-bold text-xl">{item ? "Modifier" : "Nouveau témoignage"}</h2><button type="button" onClick={onClose}><X className="w-5 h-5"/></button></div>
        <input required value={f.name} onChange={set("name")} placeholder="Nom *" className="input"/>
        <input value={f.role} onChange={set("role")} placeholder="Poste" className="input"/>
        <input value={f.company} onChange={set("company")} placeholder="Société" className="input"/>
        <textarea required rows={5} value={f.content} onChange={set("content")} placeholder="Citation *" className="input resize-none"/>
        <div className="grid grid-cols-2 gap-3">
          <input type="number" min="1" max="5" value={f.rating} onChange={set("rating")} placeholder="Note 1-5" className="input"/>
          <input type="number" value={f.order} onChange={set("order")} placeholder="Ordre" className="input"/>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} className="w-4 h-4 accent-slate-900"/>Publié</label>
        <button disabled={saving} type="submit" className="btn-brutal btn-brutal-solar w-full px-6 py-3 font-bold uppercase tracking-wider text-sm">{saving ? "..." : "Enregistrer"}</button>
      </form>
    </div>
  );
}
